import {Field, Formik, type FormikProps} from 'formik'
import queryString from 'query-string'
import React, {useCallback, useEffect, useRef, useState} from 'react'
import {useLocation, useNavigate, useParams} from 'react-router-dom'
import {ArrowUpRight, GraduationCap, KeyRound, Lock, Mail, ShieldCheck} from 'lucide-react'
import * as Yup from 'yup'
import SignInContainer from '@ui/sign-in-container/SignInContainer'
import {FormikInput, FormikPasswordInput} from '~/src/design-system-shims'
import {errorText} from '~/src/components/auth/constants/helper-text'
import {
  AUTH_MESSAGES,
  AuthBanner,
  AuthButton,
  AuthProgressView,
  AuthSuccessView,
  AuthUI as S,
  ButtonArrow,
  SUCCESS_HOLD_MS,
  playExit,
  readAuthBanner,
  swapView,
  useSignInSteps,
  wait,
  withoutAuthBanner,
  type AuthBannerState,
} from '~/src/components/auth/shell'
import StaffologyLogo from '~/src/assets/images/staffology-logo.svg'
import {VisuallyHidden} from '~/src/design-system/Primitives'
import {extractErrorMessage} from '~/src/utils/extractErrorMessage'
import {SESSION_EXPIRED_KEY, setSessionLoginTime} from '~/src/context/SessionTimeoutContext'
import type {FirstSigninResponse, IUserBase, SigninResponse, MSPAuthResponse} from '~/src/models'
import {formatRoleName} from '~/src/utils/roleDisplay'
import {
  extractUserFromToken,
  createAuthResponseFromToken,
  isTokenExpired,
} from '~/src/utils/jwt-utils'
import {BASE_ROUTES} from '~/src/routes/constants'
import {useSigninMutation} from '~/src/store/apis/auth-api'
import {useDisconnectZoomMutation} from '~/src/store/apis/zoom-config-api'
import {useLazyGetCurrentUserQuery} from '~/src/store/apis/user-api'
import {useLazyGetGaAgencyNamesAndIdsQuery} from '~/src/store/apis/member-access-api'
import {roleApi} from '~/src/services'
import {
  updateLoggedInAgencyId,
  updateLoggedInCompanyId,
  updateUserSignIn,
  updateCurrentUser,
  setBackendRolePermissions,
} from '~/src/store/slices/root-slice'
import {useAppDispatch} from '~/src/store/store-hooks'
import {isNullOrUndefined} from '~/src/utils'
import {getRouteForRole, type ModulePermission} from '~/src/utils/routeUtils'
import {
  noRolePermissions,
  resolveEffectiveModulePermissions,
} from '~/src/utils/effectiveModulePermissions'
import ExpiredPasswordDialog from './ExpiredPasswordDialog'
import {reportCatchError} from '../../utils/errorReporter'
import {buildIopAuthorizeUrl, isIopEnabled} from '~/src/utils/iop-auth'
import {consumePostSignInPath} from '~/src/utils/postSignInPath'

const TRAINING_URL = 'https://www.staffologyhelp.io'

type View = 'signin' | 'progress' | 'success'

interface SigninValues {
  email: string
  password: string
  rememberMe: boolean
}

interface LocationState {
  email?: string
}

export default function SigninPage() {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const params = useParams<{emailAddress?: string; token?: string}>()
  const locationState = location.state as LocationState | undefined
  const {search} = useLocation()

  // SHR-FE logic: Check for tempEmail and token in URL params
  const tempEmail = params?.emailAddress?.length ? params.emailAddress : undefined
  const token = params?.token?.length ? params.token : undefined

  const prefilledEmail = locationState?.email ?? tempEmail ?? ''
  const [getCurrentUser] = useLazyGetCurrentUserQuery()
  const [signin] = useSigninMutation()
  const [disconnectZoom] = useDisconnectZoomMutation()
  const [getGaAgencyNamesAndIds] = useLazyGetGaAgencyNamesAndIdsQuery()
  const [isLoading, setIsLoading] = useState(false)
  const paramValues = queryString.parse(search)
  const [expiredPasswordDialog, setExpiredPasswordDialog] = useState(false)
  const [showLegacyLogin, setShowLegacyLogin] = useState(false)
  const iopEnabled = isIopEnabled()

  const [view, setView] = useState<View>('signin')
  const [banner, setBanner] = useState<AuthBannerState | null>(() => readAuthBanner(location.state))
  const [ssoBusy, setSsoBusy] = useState(false)
  const [emailBusy, setEmailBusy] = useState(false)
  const [roleLabel, setRoleLabel] = useState<string>()
  const {
    steps: stepList,
    start: startStep,
    complete: completeStep,
    reset: resetSteps,
  } = useSignInSteps({withSession: false})
  const formikRef = useRef<FormikProps<SigninValues>>(null)
  const emailInputRef = useRef<HTMLInputElement>(null)

  const showView = useCallback((next: View) => swapView(() => setView(next)), [])

  const showSigninError = useCallback(
    (error: unknown) => {
      setIsLoading(false)
      setSsoBusy(false)
      setEmailBusy(false)
      resetSteps()
      showView('signin')
      setBanner({tone: 'error', message: extractErrorMessage(error)})
      formikRef.current?.setFieldValue('password', '', false)
    },
    [showView, resetSteps],
  )

  const handleIopSignIn = useCallback(async () => {
    try {
      setBanner(null)
      setSsoBusy(true)
      setIsLoading(true)
      const authorizeUrl = await buildIopAuthorizeUrl()
      window.location.href = authorizeUrl
    } catch (error) {
      setIsLoading(false)
      setSsoBusy(false)
      setBanner({tone: 'error', message: AUTH_MESSAGES.ssoStartFailed})
    }
  }, [])

  useEffect(() => {
    const onPageShow = (event: PageTransitionEvent) => {
      if (!event.persisted) return
      setIsLoading(false)
      setSsoBusy(false)
    }
    window.addEventListener('pageshow', onPageShow)
    return () => window.removeEventListener('pageshow', onPageShow)
  }, [])

  const fetchUserDetails = useCallback(
    async function (props: SigninResponse | FirstSigninResponse | MSPAuthResponse) {
      // Handle legacy first signin flow
      if ('email_verified' in props) {
        setIsLoading(false)
        navigate(BASE_ROUTES.PASSWORD_RESET, {
          state: {token: props.oldPassword, email: props.email, params: paramValues},
        })
        return undefined
      }
      // Handle MSP authentication response
      else if ('accessToken' in props) {
        // Extract user info from JWT token to validate we have a valid token
        const userInfo = extractUserFromToken(props.idToken)
        if (!userInfo) {
          throw new Error('Failed to extract user information from token')
        }

        // Fetch full user details from API using /user/me endpoint
        // This endpoint uses the JWT to identify the user, bypassing authorization
        const user = await getCurrentUser().unwrap()
        dispatch(updateCurrentUser(user))
        return user
      }
      // Handle legacy signin response
      else {
        const user = await getCurrentUser().unwrap()
        dispatch(updateCurrentUser(user))
        return user
      }
    },
    [dispatch, getCurrentUser, navigate, paramValues],
  )

  const fetchAndStorePermissions = useCallback(
    async function (
      user?: IUserBase,
    ): Promise<
      {user: IUserBase; category: string; modulePermissions?: ModulePermission[]} | undefined
    > {
      if (isNullOrUndefined(user)) {
        return
      }

      // If multiple roles, redirect to role selection page
      if (user.roles.length > 1) {
        navigate(BASE_ROUTES.ROLE_SELECT)
        return
      }

      // Fetch merged permissions for all user roles using the new API
      const permissionsResponse = await roleApi.getPermissionsForRoles(user.roles as string[])

      let category = 'Platform' // Default category
      let modulePermissions: ModulePermission[] | undefined

      // Map the API response to BackendRolePermissions format and store in Redux
      if (permissionsResponse.roles.length > 0) {
        const primaryRole = permissionsResponse.roles[0]
        if (primaryRole) {
          // Use role name for routing (category means "where role is defined", not "where to route")
          category = primaryRole.name

          // Permission resolution (resolveEffectiveModulePermissions, STFFLG-4243):
          // 1. Role's modulePermissions as the base
          // 2. Agency assignment overrides applied authoritatively (REPLACE — can escalate)
          // 3. User's custom modulePermissions applied restrictively on top (AND — can only restrict)
          //    (lets admins restrict individual users below their role's ceiling)
          // A role with no tree gives no module permissions: the user's own trees never stand in
          // for it, so the store keeps no tree and the role's accessList answers, as for a user
          // with no saved trees.
          modulePermissions = resolveEffectiveModulePermissions({
            roleTree: primaryRole.modulePermissions as ModulePermission[] | undefined,
            agencyTree: user.agencyAssignments?.[0]?.modulePermissions as
              | ModulePermission[]
              | undefined,
            userTree: user.modulePermissions as ModulePermission[] | undefined,
          })

          dispatch(
            setBackendRolePermissions({
              _id: primaryRole.id,
              id: primaryRole.id,
              name: primaryRole.name,
              accessList: primaryRole.accessList,
              modulePermissions: modulePermissions,
              isPlatform: primaryRole.category === 'Platform',
              // Store category for layout variant determination (custom roles need this)
              category: primaryRole.category as 'Platform' | 'MSP' | 'Agency' | 'Company',
              application: 'msp',
              createdDate: primaryRole.createdDate?.toString() ?? '',
              modifiedDate: primaryRole.modifiedDate?.toString() ?? '',
              versionKey: Number(primaryRole.versionKey) || 0,
            }),
          )

          // Store role in localStorage for compatibility
          window.localStorage.setItem('cognitoRole', JSON.stringify({role: primaryRole}))
        }
      }
      // No role found for the user's role name (a custom role the lookup could not match, or a
      // rename that left its users holding the old name): no module permissions. The user's own
      // trees used to be stored verbatim here, which made any grant saved above the role live
      // (STFFLG-4243). An explicit deny-everything role is stored (noRolePermissions): storing
      // none would let the route guard pass every route.
      if (!permissionsResponse.roles[0]) {
        dispatch(setBackendRolePermissions(noRolePermissions(user.roles[0])))
        window.localStorage.setItem(
          'cognitoRole',
          JSON.stringify({role: {id: user.roles[0], name: user.roles[0]}}),
        )
      }

      return {user, category, modulePermissions}
    },
    [dispatch, navigate],
  )

  const goToDashboard = useCallback(
    async function (result?: {
      user: IUserBase
      category: string
      modulePermissions?: ModulePermission[]
    }) {
      if (isNullOrUndefined(result)) return

      const {user, category, modulePermissions} = result

      // Set session login time for 10-hour timeout tracking
      setSessionLoginTime()

      dispatch(updateUserSignIn({data: true}))
      dispatch(updateLoggedInCompanyId(user.companyId?.length > 0 ? user.companyId : ''))

      // Determine the logged-in agency based on user type:
      // 1. Multi-agency users (agencyAssignments): auto-select the first agency
      // 2. General admin users: fetch their agency list from member access
      // 3. Single agency users: use their assigned agencyId
      const hasMultiAgencyAssignments = user.agencyAssignments && user.agencyAssignments.length > 0
      const isGeneralAdmin = user.roles?.includes('general_admin')

      if (hasMultiAgencyAssignments) {
        // Multi-agency user: auto-select the first agency assignment
        // User can switch later via the SelectAgencyMenu
        const firstAgency = user.agencyAssignments![0]
        dispatch(updateLoggedInAgencyId(firstAgency.agencyId))
      } else if (isGeneralAdmin) {
        try {
          // This will populate agencyNamesAndIds in Redux store (via agency-slice matcher)
          const agencies = await getGaAgencyNamesAndIds(user.id).unwrap()
          // Set the first agency as the logged-in agency so they have a default context
          if (agencies.length > 0) {
            dispatch(updateLoggedInAgencyId(agencies[0].id))
          } else {
            dispatch(updateLoggedInAgencyId(''))
          }
        } catch (error) {
          reportCatchError('Failed to fetch agencies for general_admin:', error)
          dispatch(updateLoggedInAgencyId(''))
        }
      } else {
        // For other roles, use their assigned agency
        dispatch(updateLoggedInAgencyId(user.agencyId?.length > 0 ? user.agencyId : ''))
      }

      // Navigate based on role name and permissions (category now holds role name), unless a
      // signing link brought them here (STFFLG-4163: rememberPostSignInPath in PrivateRoute).
      const targetRoute = consumePostSignInPath() ?? getRouteForRole(category, modulePermissions)

      completeStep('dashboard')
      setRoleLabel(formatRoleName(category))
      showView('success')
      await wait(SUCCESS_HOLD_MS)
      await playExit()
      navigate(targetRoute)
    },
    [dispatch, navigate, getGaAgencyNamesAndIds, showView, completeStep],
  )

  const completeSignIn = useCallback(
    async function (auth: SigninResponse | FirstSigninResponse | MSPAuthResponse) {
      if (!('email_verified' in auth)) {
        resetSteps()
        showView('progress')
        startStep('account')
      }
      const user = await fetchUserDetails(auth)
      if (isNullOrUndefined(user)) return
      completeStep('account')

      startStep('permissions')
      const result = await fetchAndStorePermissions(user)
      if (isNullOrUndefined(result)) return
      completeStep('permissions')

      startStep('dashboard')
      await goToDashboard(result)
    },
    [
      fetchUserDetails,
      fetchAndStorePermissions,
      goToDashboard,
      showView,
      resetSteps,
      startStep,
      completeStep,
    ],
  )

  const handleSubmit = useCallback(
    async function (emailAddress: string, password: string) {
      try {
        setBanner(null)
        setEmailBusy(true)
        setIsLoading(true)
        await signin({emailAddress: emailAddress, password: password})
          .unwrap()
          .then(async (result) => {
            try {
              await disconnectZoom().unwrap()
            } catch {
              // Best effort — Zoom may not be connected
            }
            return result
          })
          .then(completeSignIn)
      } catch (error: unknown) {
        showSigninError(error)
      }
    },
    [signin, disconnectZoom, completeSignIn, showSigninError],
  )

  const handleTokenAuth = useCallback(
    async function (passedInToken: string) {
      try {
        setIsLoading(true)

        // Validate token first
        if (isTokenExpired(passedInToken)) {
          throw new Error('Token has expired')
        }

        // Create auth response from token
        const authResponse = createAuthResponseFromToken(passedInToken)
        if (!authResponse) {
          throw new Error('Invalid token format')
        }

        // Process the token-based authentication
        await Promise.resolve(authResponse).then(completeSignIn)
      } catch (error: unknown) {
        showSigninError(error)
      }
    },
    [completeSignIn, showSigninError],
  )

  // SHR-FE redirect logic: If we have tempEmail and token, redirect to PASSWORD_RESET
  useEffect(() => {
    if (tempEmail && token) {
      navigate(BASE_ROUTES.PASSWORD_RESET, {
        state: {
          email: tempEmail,
          token: token,
        },
      })
    }
  }, [tempEmail, token, dispatch, navigate])

  useEffect(() => {
    if (readAuthBanner(location.state)) {
      navigate(`${location.pathname}${location.search}`, {
        replace: true,
        state: withoutAuthBanner(location.state),
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Check for session expired message (from auto-logout)
  useEffect(() => {
    const sessionExpired = sessionStorage.getItem(SESSION_EXPIRED_KEY)
    if (sessionExpired) {
      // Show the message and clear the flag
      setBanner({tone: 'info', message: AUTH_MESSAGES.sessionExpired})
      sessionStorage.removeItem(SESSION_EXPIRED_KEY)
    }
  }, [])

  useEffect(() => {
    if (!isLoading) {
      if (Object.keys(paramValues).length > 0) {
        // Check if we have a token parameter (from email redirect)
        if (paramValues.token) {
          void handleTokenAuth(paramValues.token as string)
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleSubmit, handleTokenAuth, paramValues, isLoading])

  useEffect(() => {
    if (iopEnabled && showLegacyLogin) emailInputRef.current?.focus()
  }, [iopEnabled, showLegacyLogin])

  const emailFormOpen = !iopEnabled || showLegacyLogin

  return (
    <SignInContainer
      below={
        view === 'signin' ? (
          <S.BelowAnchor
            href={TRAINING_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="training-link">
            <GraduationCap aria-hidden="true" />
            <span>
              New to Staffology? <strong>Explore the training</strong>
            </span>
            <ArrowUpRight className="auth-go" aria-hidden="true" />
            <VisuallyHidden>(opens in a new tab)</VisuallyHidden>
          </S.BelowAnchor>
        ) : null
      }>
      <ExpiredPasswordDialog
        isModalOpen={expiredPasswordDialog}
        onModalClose={() => {
          setExpiredPasswordDialog(false)
        }}
      />

      {view === 'progress' ? <AuthProgressView steps={stepList} fromSso={false} /> : null}
      {view === 'success' ? <AuthSuccessView roleLabel={roleLabel} /> : null}

      {view === 'signin' ? (
        <S.View data-testid="auth-view-signin">
          <S.CardLogo src={StaffologyLogo} alt="Staffology" />
          <S.CardTitle>Sign in to Staffology</S.CardTitle>
          <S.Lede>
            {iopEnabled
              ? 'Welcome back. Continue with your Single Sign account.'
              : 'Welcome back. Sign in with your email and password.'}
          </S.Lede>
          <AuthBanner banner={banner} />

          {iopEnabled && (
            <>
              <AuthButton
                type="button"
                loading={ssoBusy}
                iconLeft={<KeyRound aria-hidden="true" />}
                onClick={handleIopSignIn}
                data-testid="iop-signin-button">
                {ssoBusy ? 'Preparing secure sign-in…' : 'Sign in with Single Sign'}
              </AuthButton>
              <S.ShieldNote>
                <ShieldCheck aria-hidden="true" />
                Opens the Single Sign page, then brings you back.
              </S.ShieldNote>

              {showLegacyLogin ? (
                <S.Divider>or sign in with email</S.Divider>
              ) : (
                <S.TextButton
                  type="button"
                  onClick={() => swapView(() => setShowLegacyLogin(true))}
                  data-testid="show-legacy-login">
                  Sign in with email and password
                </S.TextButton>
              )}
            </>
          )}

          {emailFormOpen && (
            <Formik<SigninValues>
              innerRef={formikRef}
              validateOnChange={false}
              validateOnBlur={false}
              initialValues={{
                email: prefilledEmail,
                password: '',
                rememberMe: false,
              }}
              validationSchema={Yup.object({
                password: Yup.string().required(errorText.passwordRequired),
                email: Yup.string().email(errorText.emailInvalid).required(errorText.emailRequired),
              })}
              onSubmit={async function (values: {email: string; password: string}) {
                void handleSubmit(values.email, values.password)
              }}>
              {({setFieldError}) => (
                <S.EmailForm id="auth-email-form" noValidate data-testid="email-signin-form">
                  <S.Fields>
                    <FormikInput
                      ref={emailInputRef}
                      name="email"
                      label="Email"
                      type="email"
                      iconLeft={<Mail aria-hidden="true" />}
                      placeholder="name@company.com"
                      autoComplete="username"
                      onChange={() => setFieldError('email', undefined)}
                    />
                    <FormikPasswordInput
                      name="password"
                      label="Password"
                      iconLeft={<Lock aria-hidden="true" />}
                      placeholder="••••••••••"
                      autoComplete="current-password"
                      onChange={() => setFieldError('password', undefined)}
                    />
                  </S.Fields>
                  <S.FormRow>
                    <S.Check>
                      <Field type="checkbox" name="rememberMe" disabled={emailBusy} />
                      Remember me
                    </S.Check>
                    <S.TextLink to={BASE_ROUTES.FORGOT_PASSWORD}>Forgot password?</S.TextLink>
                  </S.FormRow>
                  <AuthButton type="submit" loading={emailBusy} data-testid="email-signin-button">
                    {emailBusy ? (
                      'Signing in…'
                    ) : (
                      <>
                        Sign in <ButtonArrow />
                      </>
                    )}
                  </AuthButton>
                </S.EmailForm>
              )}
            </Formik>
          )}
        </S.View>
      ) : null}
    </SignInContainer>
  )
}
